import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import Bugsnag from "@bugsnag/js";
import { getQueryParams } from "@/utils/httpRequest";
import { DEFAULT_NOTIFICATION_PAYLOAD, NOTIFICATION_VARIANTS } from "@/utils/applicationConstants";
import { ANALYTIC_EVENTS, sendAnalytics, sendCollectionToAnalytics } from "@/utils/analytics";
import { baseUrl, onPrepareHeaders } from "@/redux/services/helpers";
import { showNotification } from "@/redux/actions/notification";
import { LIST_SERVICE_TAG } from "@/shared/store/service/commonServiceTags";
import { setProcessingState } from "@/redux/reducers/modalConfirmation";
import { requestCloseModal } from "@/redux/actions/modal";
import { loanService } from "@/redux/services/loanService";
import { loanSummaryService } from "@/features/LoanSummary/Management/store/services/loanSummaryService";
import {
  LENDER_BOARD_REDUCER_PATH,
  LENDER_BOARD_SERVICE_TAG,
  LENDER_BOARD_RELATED_TAGS,
} from "@/features/LenderBoard/Management/utils/lender-board-service-tags";

export const lenderBoardService = createApi({
  reducerPath: LENDER_BOARD_REDUCER_PATH,
  tagTypes: LENDER_BOARD_RELATED_TAGS,
  baseQuery: fetchBaseQuery({
    baseUrl,
    prepareHeaders: onPrepareHeaders,
  }),
  endpoints: builder => ({
    getLenderBoards: builder.query({
      query: ({ loanId, ...params }) => `loans/${loanId}/lender-boards?${getQueryParams(params)}`,
      providesTags: result =>
        result
          ? [
              ...(result.items?.map(({ lenderBoardId }) => ({ type: LENDER_BOARD_SERVICE_TAG, id: lenderBoardId })) || []),
              { type: LENDER_BOARD_SERVICE_TAG, id: LIST_SERVICE_TAG },
            ]
          : [{ type: LENDER_BOARD_SERVICE_TAG, id: LIST_SERVICE_TAG }],
    }),

    getLenderBoard: builder.query({
      query: ({ loanId, lenderBoardId }) => `loans/${loanId}/lender-boards/${lenderBoardId}`,
      providesTags: (result, error, { lenderBoardId }) => [{ type: LENDER_BOARD_SERVICE_TAG, id: lenderBoardId }],
    }),

    createLenderBoard: builder.mutation({
      query: ({ loanId, ...body }) => ({
        url: `loans/${loanId}/lender-boards`,
        method: "POST",
        body,
      }),
      invalidatesTags: () => [{ type: LENDER_BOARD_SERVICE_TAG, id: LIST_SERVICE_TAG }],
      async onQueryStarted(queryArgs, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          sendCollectionToAnalytics(ANALYTIC_EVENTS.LENDER_BOARD_CREATED, data);
          dispatch(
            showNotification({
              ...DEFAULT_NOTIFICATION_PAYLOAD,
              message: {
                ...DEFAULT_NOTIFICATION_PAYLOAD.message,
                description: `Proposal has been sent to ${queryArgs.lenderIds?.length} lender(s).`,
                options: {
                  variant: NOTIFICATION_VARIANTS.SUCCESS,
                },
              },
            }),
          );
          dispatch(loanService.util.invalidateTags(["LoanLendersHeatMap"]));
        } catch (err) {
          Bugsnag.notify(err);
        }
      },
    }),

    createLenderBoardsCallLog: builder.mutation({
      query: ({ loanId, ...body }) => ({
        url: `loans/${loanId}/lender-boards/call-logs`,
        method: "POST",
        body,
      }),
      invalidatesTags: () => [{ type: LENDER_BOARD_SERVICE_TAG, id: LIST_SERVICE_TAG }],
      async onQueryStarted(queryArgs, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          sendCollectionToAnalytics(ANALYTIC_EVENTS.LENDER_BOARD_CREATED, data);
          dispatch(
            showNotification({
              ...DEFAULT_NOTIFICATION_PAYLOAD,
              message: {
                ...DEFAULT_NOTIFICATION_PAYLOAD.message,
                description: `${queryArgs.lenderIds?.length} lender(s) added to the Call Log.`,
                options: {
                  variant: NOTIFICATION_VARIANTS.SUCCESS,
                  buttonAction: queryArgs?.navigateToCallLog,
                  buttonLabel: queryArgs?.navigateToCallLog ? "View Call Log" : null,
                },
              },
            }),
          );
          dispatch(loanService.util.invalidateTags(["LoanLendersHeatMap"]));
        } catch (err) {
          Bugsnag.notify(err);
        }
      },
    }),

    updateLenderBoard: builder.mutation({
      query: ({ loanId, lenderBoardId, ...body }) => ({
        url: `loans/${loanId}/lender-boards/${lenderBoardId}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (result, error, { lenderBoardId }) => [{ type: LENDER_BOARD_SERVICE_TAG, id: lenderBoardId }],
      async onQueryStarted({ loanId, lenderBoardId, ...updateData }, { dispatch, queryFulfilled, getState }) {
        const state = getState();
        const queries = lenderBoardService.util.selectCachedArgsForQuery(state, "getLenderBoards");

        // Find all matching queries and update them optimistically
        const patchResults = [];

        queries.forEach(queryArgs => {
          if (queryArgs.loanId === loanId) {
            const patchResult = dispatch(
              lenderBoardService.util.updateQueryData("getLenderBoards", queryArgs, draft => {
                // Access the items array from the draft structure
                if (draft.items && Array.isArray(draft.items)) {
                  const lenderBoard = draft.items.find(board => board.lenderBoardId === lenderBoardId);
                  if (lenderBoard) {
                    // Update all provided fields from updateData
                    Object.assign(lenderBoard, { ...updateData, lastUpdatedAt: Date.now() });
                  }
                }
              }),
            );
            patchResults.push(patchResult);
          }
        });

        try {
          const { data } = await queryFulfilled;

          sendAnalytics(ANALYTIC_EVENTS.LENDER_BOARD_UPDATED, data);

          dispatch(loanSummaryService.util.invalidateTags(["LoanSummaryLenders"]));
        } catch (err) {
          // Revert all optimistic updates on error
          patchResults.forEach(patchResult => patchResult.undo());
          Bugsnag.notify(err);
        }
      },
    }),

    updateLenderBoardPackage: builder.mutation({
      query: ({ loanId, lenderBoardId, loanSummaryId, ...body }) => ({
        url: `loans/${loanId}/lender-boards/${lenderBoardId}/packages/${loanSummaryId || ""}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: result => [
        { type: LENDER_BOARD_SERVICE_TAG, id: result.lenderBoardId },
        { type: LENDER_BOARD_SERVICE_TAG, id: LIST_SERVICE_TAG },
      ],
      async onQueryStarted(queryArgs, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          sendAnalytics(ANALYTIC_EVENTS.LENDER_BOARD_UPDATED, data);
          dispatch(
            showNotification({
              ...DEFAULT_NOTIFICATION_PAYLOAD,
              message: {
                ...DEFAULT_NOTIFICATION_PAYLOAD.message,
                description: `Loan package has been sent to ${queryArgs.lenderBoardIds?.length} lender(s).`,
                options: {
                  variant: NOTIFICATION_VARIANTS.SUCCESS,
                },
              },
            }),
          );
          dispatch(loanSummaryService.util.invalidateTags(["LoanSummaryLenders", "LoanSummary"]));
          dispatch(loanService.util.invalidateTags(["LoanLendersHeatMap"]));
        } catch (err) {
          Bugsnag.notify(err);
        }
      },
    }),

    updateLenderBoardsPackage: builder.mutation({
      query: ({ loanId, ...body }) => ({
        url: `loans/${loanId}/lender-boards/packages`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: result => [
        ...(result.map(({ lenderBoardId }) => ({ type: LENDER_BOARD_SERVICE_TAG, id: lenderBoardId })) || []),
        { type: LENDER_BOARD_SERVICE_TAG, id: LIST_SERVICE_TAG },
      ],
      async onQueryStarted(queryArgs, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          sendCollectionToAnalytics(ANALYTIC_EVENTS.LENDER_BOARD_UPDATED, data);
          dispatch(
            showNotification({
              ...DEFAULT_NOTIFICATION_PAYLOAD,
              message: {
                ...DEFAULT_NOTIFICATION_PAYLOAD.message,
                description: `Loan package has been sent to ${queryArgs.lenderIds?.length} lender(s).`,
                options: {
                  variant: NOTIFICATION_VARIANTS.SUCCESS,
                },
              },
            }),
          );
          dispatch(loanSummaryService.util.invalidateTags(["LoanSummaryLenders", "LoanSummary"]));
          dispatch(loanService.util.invalidateTags(["LoanLendersHeatMap"]));
        } catch (err) {
          Bugsnag.notify(err);
        }
      },
    }),

    sendLenderBoardReminder: builder.mutation({
      query: ({ loanId, lenderBoardId, ...body }) => ({
        url: `loans/${loanId}/lender-boards/${lenderBoardId}/reminder`,
        method: "POST",
        body,
      }),
      invalidatesTags: (result, error, { lenderBoardId }) => [
        { type: LENDER_BOARD_SERVICE_TAG, id: lenderBoardId },
        { type: LENDER_BOARD_SERVICE_TAG, id: LIST_SERVICE_TAG },
      ],
      async onQueryStarted(queryArgs, { dispatch, queryFulfilled }) {
        try {
          dispatch(setProcessingState(true));
          const { data } = await queryFulfilled;

          sendAnalytics(ANALYTIC_EVENTS.LENDER_BOARD_REMINDER_SENT, data);
          dispatch(
            showNotification({
              ...DEFAULT_NOTIFICATION_PAYLOAD,
              message: {
                ...DEFAULT_NOTIFICATION_PAYLOAD.message,
                description: "Lender file reminder has been sent.",
                options: {
                  variant: NOTIFICATION_VARIANTS.SUCCESS,
                },
              },
            }),
          );
          dispatch(setProcessingState(false));
          dispatch(requestCloseModal());
        } catch (err) {
          Bugsnag.notify(err);
          dispatch(setProcessingState(false));
        }
      },
    }),

    sendLenderBoardReminders: builder.mutation({
      query: ({ loanId, ...body }) => ({
        url: `loans/${loanId}/lender-boards/reminder`,
        method: "POST",
        body,
      }),
      invalidatesTags: (result, error, { lenderBoardId }) => [
        { type: LENDER_BOARD_SERVICE_TAG, id: lenderBoardId },
        { type: LENDER_BOARD_SERVICE_TAG, id: LIST_SERVICE_TAG },
      ],
      async onQueryStarted(queryArgs, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          sendCollectionToAnalytics(ANALYTIC_EVENTS.LENDER_BOARD_REMINDER_SENT, data);
          dispatch(
            showNotification({
              ...DEFAULT_NOTIFICATION_PAYLOAD,
              message: {
                ...DEFAULT_NOTIFICATION_PAYLOAD.message,
                description: `${queryArgs.lenderBoardIds.length} lender file(s) reminder have been sent.`,
                options: {
                  variant: NOTIFICATION_VARIANTS.SUCCESS,
                },
              },
            }),
          );
          dispatch(loanService.util.invalidateTags(["LoanLendersHeatMap"]));
        } catch (err) {
          Bugsnag.notify(err);
        }
      },
    }),

    approveLenderBoard: builder.mutation({
      query: ({ loanId, lenderBoardId, ...body }) => ({
        url: `loans/${loanId}/lender-boards/${lenderBoardId}/approval`,
        method: "POST",
        body,
      }),
      invalidatesTags: (result, error, { lenderBoardId }) => [
        { type: LENDER_BOARD_SERVICE_TAG, id: lenderBoardId },
        { type: LENDER_BOARD_SERVICE_TAG, id: LIST_SERVICE_TAG },
      ],
      async onQueryStarted(queryArgs, { dispatch, queryFulfilled }) {
        try {
          dispatch(setProcessingState(true));
          const { data } = await queryFulfilled;

          sendAnalytics(ANALYTIC_EVENTS.LENDER_BOARD_APPROVED, data);
          dispatch(
            showNotification({
              ...DEFAULT_NOTIFICATION_PAYLOAD,
              message: {
                ...DEFAULT_NOTIFICATION_PAYLOAD.message,
                description: "Lender file has been approved.",
                options: {
                  variant: NOTIFICATION_VARIANTS.SUCCESS,
                },
              },
            }),
          );
          dispatch(setProcessingState(false));
          dispatch(requestCloseModal());
        } catch (err) {
          Bugsnag.notify(err);
          dispatch(setProcessingState(false));
        }
      },
    }),

    approveLenderBoards: builder.mutation({
      query: ({ loanId, ...body }) => ({
        url: `loans/${loanId}/lender-boards/approval`,
        method: "POST",
        body,
      }),
      invalidatesTags: (result, error, { lenderBoardId }) => [
        { type: LENDER_BOARD_SERVICE_TAG, id: lenderBoardId },
        { type: LENDER_BOARD_SERVICE_TAG, id: LIST_SERVICE_TAG },
      ],
      async onQueryStarted(queryArgs, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          sendCollectionToAnalytics(ANALYTIC_EVENTS.LENDER_BOARD_APPROVED, data);
          dispatch(
            showNotification({
              ...DEFAULT_NOTIFICATION_PAYLOAD,
              message: {
                ...DEFAULT_NOTIFICATION_PAYLOAD.message,
                description: `${queryArgs.lenderBoardIds.length} lender file(s) have been approved.`,
                options: {
                  variant: NOTIFICATION_VARIANTS.SUCCESS,
                },
              },
            }),
          );
          dispatch(loanService.util.invalidateTags(["LoanLendersHeatMap"]));
        } catch (err) {
          Bugsnag.notify(err);
        }
      },
    }),

    closeLenderBoard: builder.mutation({
      query: ({ loanId, lenderBoardId, ...body }) => ({
        url: `loans/${loanId}/lender-boards/${lenderBoardId}/closure`,
        method: "POST",
        body,
      }),
      invalidatesTags: (result, error, { lenderBoardId }) => [
        { type: LENDER_BOARD_SERVICE_TAG, id: lenderBoardId },
        { type: LENDER_BOARD_SERVICE_TAG, id: LIST_SERVICE_TAG },
      ],
      async onQueryStarted(queryArgs, { dispatch, queryFulfilled }) {
        try {
          dispatch(setProcessingState(true));
          const { data } = await queryFulfilled;

          sendAnalytics(ANALYTIC_EVENTS.LENDER_BOARD_CLOSED, data);
          dispatch(
            showNotification({
              ...DEFAULT_NOTIFICATION_PAYLOAD,
              message: {
                ...DEFAULT_NOTIFICATION_PAYLOAD.message,
                description: "Lender file has been closed.",
                options: {
                  variant: NOTIFICATION_VARIANTS.SUCCESS,
                },
              },
            }),
          );
          dispatch(setProcessingState(false));
          dispatch(requestCloseModal());
        } catch (err) {
          Bugsnag.notify(err);
          dispatch(setProcessingState(false));
        }
      },
    }),

    closeLenderBoards: builder.mutation({
      query: ({ loanId, ...body }) => ({
        url: `loans/${loanId}/lender-boards/closure`,
        method: "POST",
        body,
      }),
      invalidatesTags: (result, error, { lenderBoardId }) => [
        { type: LENDER_BOARD_SERVICE_TAG, id: lenderBoardId },
        { type: LENDER_BOARD_SERVICE_TAG, id: LIST_SERVICE_TAG },
      ],
      async onQueryStarted(queryArgs, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          sendCollectionToAnalytics(ANALYTIC_EVENTS.LENDER_BOARD_CLOSED, data);
          dispatch(
            showNotification({
              ...DEFAULT_NOTIFICATION_PAYLOAD,
              message: {
                ...DEFAULT_NOTIFICATION_PAYLOAD.message,
                description: `${queryArgs.lenderBoardIds.length} lender file(s) have been closed.`,
                options: {
                  variant: NOTIFICATION_VARIANTS.SUCCESS,
                },
              },
            }),
          );
          dispatch(loanService.util.invalidateTags(["LoanLendersHeatMap"]));
        } catch (err) {
          Bugsnag.notify(err);
        }
      },
    }),

    reopenLenderBoard: builder.mutation({
      query: ({ loanId, lenderBoardId, ...body }) => ({
        url: `loans/${loanId}/lender-boards/${lenderBoardId}/reopening`,
        method: "POST",
        body,
      }),
      invalidatesTags: (result, error, { lenderBoardId }) => [
        { type: LENDER_BOARD_SERVICE_TAG, id: lenderBoardId },
        { type: LENDER_BOARD_SERVICE_TAG, id: LIST_SERVICE_TAG },
      ],
      async onQueryStarted(queryArgs, { dispatch, queryFulfilled }) {
        try {
          dispatch(setProcessingState(true));
          const { data } = await queryFulfilled;

          sendAnalytics(ANALYTIC_EVENTS.LENDER_BOARD_REOPENED, data);
          dispatch(
            showNotification({
              ...DEFAULT_NOTIFICATION_PAYLOAD,
              message: {
                ...DEFAULT_NOTIFICATION_PAYLOAD.message,
                description: "Lender board has been reopened!",
                options: {
                  variant: NOTIFICATION_VARIANTS.SUCCESS,
                },
              },
            }),
          );
          dispatch(setProcessingState(false));
          dispatch(requestCloseModal());
        } catch (err) {
          Bugsnag.notify(err);
          dispatch(setProcessingState(false));
        }
      },
    }),

    reopenLenderBoards: builder.mutation({
      query: ({ loanId, ...body }) => ({
        url: `loans/${loanId}/lender-boards/reopening`,
        method: "POST",
        body,
      }),
      invalidatesTags: (result, error, { lenderBoardId }) => [
        { type: LENDER_BOARD_SERVICE_TAG, id: lenderBoardId },
        { type: LENDER_BOARD_SERVICE_TAG, id: LIST_SERVICE_TAG },
      ],
      async onQueryStarted(queryArgs, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          sendCollectionToAnalytics(ANALYTIC_EVENTS.LENDER_BOARD_REOPENED, data);
          dispatch(
            showNotification({
              ...DEFAULT_NOTIFICATION_PAYLOAD,
              message: {
                ...DEFAULT_NOTIFICATION_PAYLOAD.message,
                description: `${queryArgs.lenderBoardIds.length} lender file(s) have been reopened.`,
                options: {
                  variant: NOTIFICATION_VARIANTS.SUCCESS,
                },
              },
            }),
          );
          dispatch(loanService.util.invalidateTags(["LoanLendersHeatMap"]));
        } catch (err) {
          Bugsnag.notify(err);
        }
      },
    }),

    deleteLenderBoards: builder.mutation({
      query: ({ loanId, ...body }) => ({
        url: `loans/${loanId}/lender-boards`,
        method: "DELETE",
        body,
      }),
      invalidatesTags: [{ type: LENDER_BOARD_SERVICE_TAG, id: LIST_SERVICE_TAG }],
      async onQueryStarted(queryArgs, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          sendCollectionToAnalytics(ANALYTIC_EVENTS.LENDER_BOARD_DELETED, data);
          dispatch(
            showNotification({
              ...DEFAULT_NOTIFICATION_PAYLOAD,
              message: {
                ...DEFAULT_NOTIFICATION_PAYLOAD.message,
                description: `${queryArgs.lenderBoardIds.length} lender(s) have been removed from call log.`,
                options: {
                  variant: NOTIFICATION_VARIANTS.SUCCESS,
                },
              },
            }),
          );
          dispatch(loanService.util.invalidateTags(["LoanLendersHeatMap"]));
        } catch (err) {
          Bugsnag.notify(err);
        }
      },
    }),
  }),
});

export const {
  useGetLenderBoardsQuery,
  useGetLenderBoardQuery,
  useCreateLenderBoardMutation,
  useCreateLenderBoardsCallLogMutation,
  useUpdateLenderBoardMutation,
  useUpdateLenderBoardPackageMutation,
  useUpdateLenderBoardsPackageMutation,
  useSendLenderBoardReminderMutation,
  useSendLenderBoardRemindersMutation,
  useApproveLenderBoardMutation,
  useApproveLenderBoardsMutation,
  useCloseLenderBoardMutation,
  useCloseLenderBoardsMutation,
  useReopenLenderBoardMutation,
  useReopenLenderBoardsMutation,
  useDeleteLenderBoardsMutation,
} = lenderBoardService;
